<template>
  <div>
    <div class="d-flex flex-wrap align-center justify-space-between" style="gap: 12px;">
      <h2 class="mb-0">Relatório Geral de Pedido de Compra</h2>
      <v-btn outlined color="primary" @click="$router.push('/relatorios#financeiro')">
        <v-icon left>mdi-arrow-left</v-icon>
        Voltar ao menu
      </v-btn>
    </div>

    <v-row class="mt-3" dense>
      <v-col cols="6" md="3"><v-card outlined class="pa-3 kpi"><div class="kpi-t">Pedidos</div><div class="kpi-v">{{ linhas.length }}</div></v-card></v-col>
      <v-col cols="6" md="3"><v-card outlined class="pa-3 kpi"><div class="kpi-t">Valor original</div><div class="kpi-v">{{ formatarMoedaPtBr(totais.valorOriginal) }}</div></v-card></v-col>
      <v-col cols="6" md="3"><v-card outlined class="pa-3 kpi"><div class="kpi-t">Valor liquidado</div><div class="kpi-v">{{ formatarMoedaPtBr(totais.valorPago) }}</div></v-card></v-col>
      <v-col cols="6" md="3"><v-card outlined class="pa-3 kpi kpi-red"><div class="kpi-t">Pedidos vencidos</div><div class="kpi-v">{{ qtdVencidos }}</div></v-card></v-col>
    </v-row>

    <div class="d-flex flex-wrap align-center mt-4 mb-2" style="gap: 12px;">
      <v-text-field v-model="filtros.pedido" label="Pedido" solo hide-details clearable class="c-filtro" />
      <v-text-field v-model="filtros.fornecedor" label="Fornecedor" solo hide-details clearable class="c-filtro" />
      <v-text-field v-model="filtros.job" label="Job" solo hide-details clearable class="c-filtro" />
      <v-select v-model="filtros.status" :items="opcoesStatus" label="Status" solo hide-details clearable multiple class="c-filtro" />
      <v-select v-model="filtros.recebimento" :items="opcoesRecebimento" label="Situação do Recebimento" solo hide-details clearable class="c-filtro" />
    </div>
    <div class="d-flex flex-wrap align-center mb-4" style="gap: 12px;">
      <v-text-field v-model="filtros.emissaoDe" type="date" label="Emissão de" outlined dense background-color="white" hide-details clearable class="c-filtro-data" />
      <v-text-field v-model="filtros.emissaoAte" type="date" label="Emissão até" outlined dense background-color="white" hide-details clearable class="c-filtro-data" />
      <v-text-field v-model="filtros.vencimentoDe" type="date" label="Vencimento de" outlined dense background-color="white" hide-details clearable class="c-filtro-data" />
      <v-text-field v-model="filtros.vencimentoAte" type="date" label="Vencimento até" outlined dense background-color="white" hide-details clearable class="c-filtro-data" />
      <v-text-field v-model="filtros.pagamentoDe" type="date" label="Últ. pagamento de" outlined dense background-color="white" hide-details clearable class="c-filtro-data" />
      <v-text-field v-model="filtros.pagamentoAte" type="date" label="Últ. pagamento até" outlined dense background-color="white" hide-details clearable class="c-filtro-data" />
      <v-checkbox v-model="filtros.somenteVencidos" label="Somente vencidos" color="red" hide-details class="mt-0" />
      <v-checkbox v-model="filtros.comSaldo" label="Com saldo a liquidar" hide-details class="mt-0" />

      <v-btn color="primary" :loading="loading" @click="buscarDados">
        <v-icon left>mdi-refresh</v-icon>
        Recarregar
      </v-btn>
      <v-menu>
        <template v-slot:activator="{ on }">
          <v-btn color="primary" outlined v-on="on" :disabled="loading || linhas.length === 0">
            <v-icon left>mdi-file-export-outline</v-icon>
            Exportar
          </v-btn>
        </template>
        <v-list>
          <v-list-item link @click="gerarExcel"><v-list-item-title>Excel</v-list-item-title></v-list-item>
          <v-list-item link @click="gerarPdf"><v-list-item-title>PDF</v-list-item-title></v-list-item>
        </v-list>
      </v-menu>
    </div>

    <v-data-table
      :headers="headers"
      :items="linhas"
      :loading="loading"
      item-key="_id"
      show-expand
      single-expand
      :footer-props="{ 'items-per-page-text': 'Linhas por página:', 'page-text': '{0}-{1} de {2}', 'items-per-page-options': [25, 50, 100, -1] }"
      :items-per-page="50"
      class="elevation-1 tabela-geral"
    >
      <template v-slot:item="{ item, expand, isExpanded, headers: hs }">
        <tr :class="item.vencido ? 'linha-vencida' : ''">
          <td v-for="h in hs" :key="h.value" :class="h.align === 'end' ? 'text-end' : ''">
            <template v-if="h.value === 'data-table-expand'">
              <v-btn icon small @click="expand(!isExpanded)"><v-icon>{{ isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon></v-btn>
            </template>
            <template v-else-if="['dataEmissao','dataUltimoPagamento','dataUltimaRevisao'].includes(h.value)">{{ formatarDia(item[h.value]) }}</template>
            <template v-else-if="h.value === 'vencimentos'">{{ item.vencimentos.map(formatarDia).join(', ') }}</template>
            <template v-else-if="CAMPOS_VALOR.includes(h.value)">{{ formatarMoedaPtBr(item[h.value]) }}</template>
            <template v-else-if="h.value === 'diasAtraso'"><span v-if="item.diasAtraso" class="red--text font-weight-bold">{{ item.diasAtraso }}</span></template>
            <template v-else>{{ item[h.value] }}</template>
          </td>
        </tr>
      </template>

      <template v-slot:expanded-item="{ item, headers: hs }">
        <td :colspan="hs.length" class="py-2 grey lighten-4">
          <div v-if="!item.parcelas.length" class="parcela-linha">Sem conta a pagar gerada para este pedido.</div>
          <div v-for="(p, i) in item.parcelas" :key="i" class="parcela-linha">
            <b>Parcela {{ i + 1 }}</b> — vence {{ formatarDia(p.vencimento) }} · {{ p.status }} · original {{ formatarMoedaPtBr(p.valorOriginal) }} ·
            liquidado {{ formatarMoedaPtBr(p.valorPago) }} · a liquidar {{ formatarMoedaPtBr(p.valorParaPagar) }}
          </div>
        </td>
      </template>

      <template v-slot:body.append>
        <tr class="linha-total">
          <td :colspan="9">TOTAL (filtro aplicado)</td>
          <td v-for="c in CAMPOS_VALOR" :key="c" class="text-end">{{ formatarMoedaPtBr(totais[c]) }}</td>
          <td colspan="2"></td>
        </tr>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import relatorioService from "@/views/Relatorios/services/relatorioPedidoDeCompraGeralService";
import { formatarDia } from "@/views/Relatorios/services/relatorioGeralUtils";
import { exportarPdfTabela, formatarMoedaPtBr, normalizarTexto } from "@/views/Relatorios/base/relatorioBaseUtils";
import relatorioPermissionMixin from "@/mixins/relatorioPermissionMixin";

const CAMPOS_VALOR = [
  "valorOriginal", "valorParaReservar", "valorDesconto", "valorPagamento", "valorReservado",
  "valorParaPagar", "valorPago", "valorParaVincular", "valorVinculado"
];

export default {
  name: "RelatorioPedidoDeCompraGeralView",
  mixins: [relatorioPermissionMixin],
  data() {
    return {
      relatorioSchemas: ["PedidoDeCompra", "FinPay"],
      relatorioFiltrarPorOwner: false,
      loading: false,
      dados: [],
      CAMPOS_VALOR,
      filtros: {
        pedido: "", fornecedor: "", job: "", status: [], recebimento: null,
        emissaoDe: null, emissaoAte: null, vencimentoDe: null, vencimentoAte: null,
        pagamentoDe: null, pagamentoAte: null, somenteVencidos: false, comSaldo: false
      },
      headers: [
        { text: "Pedido", value: "pedido" },
        { text: "Fornecedor", value: "fornecedor" },
        { text: "Job", value: "job" },
        { text: "Data de Emissão", value: "dataEmissao" },
        { text: "Datas de Vencimento", value: "vencimentos", sortable: false },
        { text: "Data do Último Pagamento", value: "dataUltimoPagamento" },
        { text: "Data da Última Revisão", value: "dataUltimaRevisao" },
        { text: "Status", value: "status" },
        { text: "Situação do Recebimento", value: "recebimento" },
        { text: "Valor Original", value: "valorOriginal", align: "end" },
        { text: "Valor a Reservar", value: "valorParaReservar", align: "end" },
        { text: "Soma dos Descontos", value: "valorDesconto", align: "end" },
        { text: "Soma dos Pagamentos", value: "valorPagamento", align: "end" },
        { text: "Valor Reservado", value: "valorReservado", align: "end" },
        { text: "Valor a Liquidar", value: "valorParaPagar", align: "end" },
        { text: "Valor Liquidado", value: "valorPago", align: "end" },
        { text: "Valor a Vincular", value: "valorParaVincular", align: "end" },
        { text: "Valor Vinculado", value: "valorVinculado", align: "end" },
        { text: "Dias em atraso", value: "diasAtraso", align: "end" },
        { text: "", value: "data-table-expand", sortable: false }
      ]
    };
  },
  computed: {
    opcoesStatus() { return Array.from(new Set(this.dados.map((d) => d.status).filter(Boolean))).sort(); },
    opcoesRecebimento() { return Array.from(new Set(this.dados.map((d) => d.recebimento).filter(Boolean))).sort(); },
    linhas() {
      const f = this.filtros;
      const txt = (v) => normalizarTexto(v).toLowerCase();
      const noPeriodo = (dia, de, ate) => {
        if (!de && !ate) return true;
        if (!dia) return false;
        return (!de || dia >= de) && (!ate || dia <= ate);
      };
      return this.dados.filter((d) => {
        if (f.pedido && !txt(d.pedido).includes(txt(f.pedido))) return false;
        if (f.fornecedor && !txt(d.fornecedor).includes(txt(f.fornecedor))) return false;
        if (f.job && !txt(d.job).includes(txt(f.job))) return false;
        if (f.status && f.status.length && !f.status.includes(d.status)) return false;
        if (f.recebimento && d.recebimento !== f.recebimento) return false;
        if (!noPeriodo(d.dataEmissao, f.emissaoDe, f.emissaoAte)) return false;
        if (!noPeriodo(d.dataUltimoPagamento, f.pagamentoDe, f.pagamentoAte)) return false;
        if ((f.vencimentoDe || f.vencimentoAte) && !d.vencimentos.some((v) => noPeriodo(v, f.vencimentoDe, f.vencimentoAte))) return false;
        if (f.somenteVencidos && !d.vencido) return false;
        if (f.comSaldo && !(d.valorOriginal - d.valorPago > 0.009)) return false;
        return true;
      });
    },
    totais() {
      const t = {};
      CAMPOS_VALOR.forEach((c) => { t[c] = this.linhas.reduce((a, d) => a + d[c], 0); });
      return t;
    },
    qtdVencidos() { return this.linhas.filter((d) => d.vencido).length; },
    descricaoFiltros() {
      const f = this.filtros;
      const p = [];
      if (f.pedido) p.push(`Pedido: ${f.pedido}`);
      if (f.fornecedor) p.push(`Fornecedor: ${f.fornecedor}`);
      if (f.job) p.push(`Job: ${f.job}`);
      if (f.status && f.status.length) p.push(`Status: ${f.status.join(", ")}`);
      if (f.recebimento) p.push(`Recebimento: ${f.recebimento}`);
      if (f.emissaoDe || f.emissaoAte) p.push(`Emissão: ${formatarDia(f.emissaoDe) || "..."} a ${formatarDia(f.emissaoAte) || "..."}`);
      if (f.vencimentoDe || f.vencimentoAte) p.push(`Vencimento: ${formatarDia(f.vencimentoDe) || "..."} a ${formatarDia(f.vencimentoAte) || "..."}`);
      if (f.pagamentoDe || f.pagamentoAte) p.push(`Últ. pagamento: ${formatarDia(f.pagamentoDe) || "..."} a ${formatarDia(f.pagamentoAte) || "..."}`);
      if (f.somenteVencidos) p.push("Somente vencidos");
      if (f.comSaldo) p.push("Com saldo a liquidar");
      return p.length ? p.join(" | ") : "Sem filtros";
    }
  },
  methods: {
    formatarMoedaPtBr,
    formatarDia,
    async buscarDados() {
      this.loading = true;
      try {
        const lista = await relatorioService.listar();
        this.dados = Array.isArray(lista) ? lista : [];
      } catch (e) {
        this.dados = [];
        this.$notfy?.error?.("Não foi possível carregar o relatório de pedidos de compra.");
      } finally {
        this.loading = false;
      }
    },
    gerarExcel() {
      if (!this.linhas.length) return;
      const pedidos = this.linhas.map((d) => ({
        Pedido: d.pedido,
        Fornecedor: d.fornecedor,
        Job: d.job,
        "Data de Emissão": formatarDia(d.dataEmissao),
        "Datas de Vencimento": d.vencimentos.map(formatarDia).join(", "),
        "Data do Último Pagamento": formatarDia(d.dataUltimoPagamento),
        "Data da Última Revisão": formatarDia(d.dataUltimaRevisao),
        Status: d.status,
        "Situação do Recebimento": d.recebimento,
        "Valor Original": d.valorOriginal,
        "Valor a Reservar": d.valorParaReservar,
        "Soma dos Descontos": d.valorDesconto,
        "Soma dos Pagamentos": d.valorPagamento,
        "Valor Reservado": d.valorReservado,
        "Valor a Liquidar": d.valorParaPagar,
        "Valor Liquidado": d.valorPago,
        "Valor a Vincular": d.valorParaVincular,
        "Valor Vinculado": d.valorVinculado,
        "Dias em atraso": d.diasAtraso || ""
      }));
      const total = { Pedido: "TOTAL" };
      CAMPOS_VALOR.forEach((c) => {
        const rotulo = this.headers.find((h) => h.value === c).text;
        total[rotulo] = Number(this.totais[c].toFixed(2));
      });
      pedidos.push(total);

      const parcelas = [];
      this.linhas.forEach((d) => d.parcelas.forEach((p, i) => parcelas.push({
        Pedido: d.pedido, Fornecedor: d.fornecedor, Parcela: i + 1, Vencimento: formatarDia(p.vencimento),
        Situação: p.status, "Valor Original": p.valorOriginal, "Valor Liquidado": p.valorPago, "Valor a Liquidar": p.valorParaPagar
      })));

      const itens = [];
      this.linhas.forEach((d) => d.itens.forEach((i) => itens.push({
        Pedido: d.pedido, Fornecedor: d.fornecedor, Item: i.item, Código: i.codigo, Descrição: i.descricao,
        Job: i.job, OS: i.os, Quantidade: i.quantidade, "Valor Unitário": i.valorUnitario, "Valor Total": i.valorTotal
      })));

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(pedidos), "Pedidos");
      if (parcelas.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(parcelas), "Parcelas");
      if (itens.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(itens), "Itens");
      XLSX.writeFile(wb, "Relatório Geral de Pedido de Compra.xlsx");
    },
    async gerarPdf() {
      if (!this.linhas.length) return;
      const formatadores = {
        dataEmissao: formatarDia, dataUltimoPagamento: formatarDia, dataUltimaRevisao: formatarDia,
        vencimentos: (v) => (v || []).map(formatarDia).join(", "),
        diasAtraso: (v) => (v ? String(v) : "")
      };
      CAMPOS_VALOR.forEach((c) => { formatadores[c] = (v) => formatarMoedaPtBr(v); });
      await exportarPdfTabela({
        headers: this.headers.filter((h) => h.value !== "data-table-expand"),
        rows: this.linhas,
        nomeArquivo: "Relatório Geral de Pedido de Compra.pdf",
        tituloRelatorio: "Relatório Geral de Pedido de Compra",
        subtituloRelatorioLinhas: [this.descricaoFiltros],
        pageFormat: "a3",
        estilos: { fontSize: 6.5 },
        formatadoresPorCampo: formatadores,
        resumoFinalLinhas: [
          `Pedidos: ${this.linhas.length} | Vencidos: ${this.qtdVencidos}`,
          `Valor original: ${formatarMoedaPtBr(this.totais.valorOriginal)} | Liquidado: ${formatarMoedaPtBr(this.totais.valorPago)} | A liquidar: ${formatarMoedaPtBr(this.totais.valorParaPagar)}`
        ]
      });
    }
  }
  // A busca inicial é disparada pelo relatorioPermissionMixin assim que o acesso é confirmado.
};
</script>

<style scoped>
.c-filtro { max-width: 220px !important; }
.c-filtro-data { max-width: 200px !important; }
::v-deep .v-data-table-header th { white-space: nowrap; vertical-align: middle; }
.kpi { border-left: 4px solid #1976d2 !important; }
.kpi-red { border-left-color: #d32f2f !important; }
.kpi-t { font-size: 12px; color: #666; }
.kpi-v { font-size: 20px; font-weight: 600; }
.linha-vencida { background: #ffebee; }
.linha-total td { font-weight: 700; background: #e8f0fe; }
.parcela-linha { font-size: 13px; padding: 2px 0; }
.tabela-geral { overflow-x: auto; }
</style>
